import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import cn from 'clsx'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import Pill from 'components/Luxkit/Pill'
import config from 'constants/config'
import FormatCurrency from 'components/Common/FormatCurrency'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import TextLink from 'components/Luxkit/TextLink'
import TaxesAndFees from 'components/OfferPage/PackageOption/Pricing/TaxesAndFees'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import moment from 'moment'
import GeoContext from 'contexts/geoContext'
import useHotelPriceColour from 'hooks/useHotelPriceColour'
import PriceDetailsModal from 'components/Common/PriceDetails/PriceDetailsModal'
import TextButton from 'components/Luxkit/Button/TextButton'
import BusinessTravellerOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerOfferCreditsTextLink'
import { pluralizeToString } from 'lib/string/pluralize'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Divider from 'components/Luxkit/Divider'
import noop from 'lib/function/noop'

const PriceContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(12)};

  &.two-column {
    grid-template-columns:1fr 1px 1fr;
  }

  ${mediaQueryUp.tablet} {
    grid-template-columns: ${rem(180)};


    &.two-column {
      grid-template-columns: ${rem(180)} 1px ${rem(180)};
    }
  }
`

interface Props {
  promotions?: App.BedbankPromotion;
  rate: App.BedbankRate;
  rooms: Array<App.Occupants>;
  offer: App.BedbankOffer | App.BedbankOfferSummary;
  flightOrigin?: App.Airport;
  flightPrice?: number;
  checkIn?: moment.Moment;
  departureTooEarly?: boolean;
  flightsEnabled: boolean;
  onBookNow?: (rate: App.BedbankRate, flights?: boolean) => void;
}

function BedbankPriceDetails(props: Props) {
  const {
    rate,
    promotions,
    rooms,
    offer,
    checkIn,
    flightOrigin,
    flightPrice,
    departureTooEarly,
    flightsEnabled,
    onBookNow = noop,
  } = props

  const { showTaxExclusivePrices } = useContext(GeoContext)
  const offerPageDispatch = useOfferPageDispatch()
  const hasFlights = !!flightPrice
  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterTypes })
  }, [offerPageDispatch])

  const {
    taxesAndFees,
    propertyFees = 0,
  } = rate.totals

  const hotelPrice = showTaxExclusivePrices ? rate.totals.exclusive : rate.totals.inclusive
  const exclusivePriceColor = useHotelPriceColour(taxesAndFees)
  const totalPrice = showTaxExclusivePrices ? hotelPrice : hotelPrice + propertyFees

  const departureTooFarInFuture = useMemo(() => {
    if (checkIn) {
      return checkIn.diff(moment.utc(), 'days') > 330
    }
    return false
  }, [checkIn])

  const hasDates = !!checkIn

  const isPromoRate = rate.id === promotions?.id

  return <VerticalSpacer gap={8}>
    {rate.discount > config.displayDiscountThreshold.bedbank &&
      <Pill kind="primary" variant="success">
        Save up to {Math.round(rate.discount * 100)}%
      </Pill>
    }
    <PriceContent className={cn({ 'two-column': flightsEnabled })}>
      <Group
        direction={flightsEnabled ? 'vertical' : 'horizontal'}
        tabletDirection="vertical"
        verticalAlign={flightsEnabled ? undefined : 'end'}
        horizontalAlign={flightsEnabled ? undefined : 'space-between'}
        tabletVerticalAlign={flightsEnabled ? 'space-between' : 'start'}
        gap={20}
      >
        <VerticalSpacer gap={8}>
          {flightsEnabled && <Caption variant="large" weight="bold">Hotel</Caption>}
          <div>
            <Caption variant="large">
              Total for {pluralizeToString('night', rate.nights)}{hasDates ? ' from' : ''}
            </Caption>
            <Heading variant="heading5" colour={exclusivePriceColor}>
              <FormatCurrency format="roundedDollar" value={totalPrice} />
            </Heading>
            {!isPromoRate && <TaxesAndFees taxAmount={taxesAndFees} />}
            {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
              type="exact"
              offer={offer}
              totalCost={totalPrice}
              numberOfNights={rate.nights}
            />}
            {rate.discount > 0 && <Caption variant="medium" weight="bold">
              Valued up to <FormatCurrency format="roundedDollar" value={rate.value} />
            </Caption>
            }
            {!isPromoRate && <PriceDetailsModal
              offerPage
              offer={offer}
              rooms={rooms}
              propertyFees={propertyFees}
              hotelPrice={hotelPrice}
              taxesAndFees={taxesAndFees}
              duration={rate.nights}
              cta={
                <TextButton kind="primary" fit="flex">
                  {hasDates ? 'Book now' : 'Check availability'}
                </TextButton>
              }
              onCtaAfterClose={() => onBookNow(rate)}
            />}
          </div>
        </VerticalSpacer>
        <TextButton
          kind="primary"
          data-testid="book-button"
          fit={flightsEnabled ? 'full-width' : 'desktop-full-width'}
          onClick={() => onBookNow(rate)}
        >
          {hasDates ? 'Book now' : 'Check availability'}
        </TextButton>
      </Group>
      {flightsEnabled && <Divider kind="primary" direction="vertical" />}
      {flightsEnabled && <Group direction="vertical" verticalAlign="space-between" gap={20}>
        <VerticalSpacer gap={8}>
          <Caption variant="large" weight="bold">
            Hotel + flights
          </Caption>
          <div>
            {!hasFlights && flightOrigin && flightOrigin.code === offer.flights?.airportCode && (
              <div>
                <Caption variant="large" colour="neutral-three">
                  No flights available from {flightOrigin.name}. Please select another airport.
                </Caption>
                <TextLink
                  onClick={onToggleFilter}
                  data-filter="airport"
                >
                  Change departure airport
                </TextLink>
              </div>
            )}
            {!hasFlights && flightOrigin?.code !== offer.flights?.airportCode && !departureTooFarInFuture && !departureTooEarly && (
              <Caption variant="large" colour="neutral-three">
                Add flights to the hotel package. Select from all major airlines at the next step.
              </Caption>
            )}
            {!hasFlights && departureTooFarInFuture && (
              <Caption variant="large" colour="neutral-three">
                Flights can only be booked up to 11 months in advance.
              </Caption>
            )}
            {flightOrigin?.code !== offer.flights?.airportCode && departureTooEarly && (
              <div>
                <Caption variant="large" colour="neutral-three">
                  Flights cannot be booked online when check-in is within 24 hours
                </Caption>
                <TextLink
                  onClick={onToggleFilter}
                  data-filter="datepicker"
                >
                  Change departure date
                </TextLink>
              </div>
            )}
            {hasFlights && !departureTooEarly && <div>
              <Caption variant="large">Total for {pluralizeToString('night', rate.nights)}{hasDates ? ' from' : ''}</Caption>
              <Heading variant="heading5" colour={exclusivePriceColor}>
                <FormatCurrency format="roundedDollar" value={totalPrice + flightPrice} /><sup>*</sup>
              </Heading>
              <TaxesAndFees taxAmount={taxesAndFees} />
              {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
                type="exact"
                offer={offer}
                totalCost={totalPrice}
                numberOfNights={rate.nights}
              />}
              {rate.discount > 0 && !isPromoRate &&
                <Caption variant="medium" weight="bold">
                  Valued up to <FormatCurrency format="roundedDollar" value={rate.value + flightPrice} />
                </Caption>
                }
              {!isPromoRate && <PriceDetailsModal
                offerPage
                offer={offer}
                rooms={rooms}
                propertyFees={propertyFees}
                hotelPrice={hotelPrice}
                taxesAndFees={taxesAndFees}
                flightPrice={flightPrice}
                duration={rate.nights}
                cta={
                  <TextButton kind="primary" fit="flex">
                    {hasDates ? 'Book now' : 'Check availability'}
                  </TextButton>
                }
                onCtaAfterClose={() => onBookNow(rate, true)}
              />}
            </div>}
            <AtolProtectedTooltip />
          </div>
        </VerticalSpacer>
        <TextButton
          kind="primary"
          data-testid="book-button"
          fit="flex"
          onClick={() => onBookNow(rate, true)}
        >
          {hasDates ? 'Book now' : 'Check availability'}
        </TextButton>
      </Group>}
    </PriceContent>
  </VerticalSpacer>
}

export default BedbankPriceDetails
